<template>
    <div class="sticky">
        <div class="sticky-in">
            <div class="title-box">
                <!-- Titre éditable en place : crayon discret pour signaler l'édition, il donne le focus au champ -->
                <div class="title-wrap">
                    <input class="title-in" type="text" :value="name" placeholder="Titre de la recette"
                           aria-label="Titre de la recette" id="recipe-name" ref="title"
                           @input="$emit('update:name', $event.target.value)">
                    <button type="button" class="title-edit" aria-label="Modifier le titre" title="Modifier le titre" @click="$refs.title.focus()">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                    </button>
                </div>
                <div class="save-state" :class="{error: saveState === 'error'}" aria-live="polite">
                    <span v-if="saveStateText" class="dot" :class="'dot--' + (saveState || 'idle')" aria-hidden="true"></span>{{ saveStateText }}
                    <button v-if="saveState === 'error'" type="button" class="link-btn" @click="$emit('retry')">Réessayer</button>
                </div>
            </div>
            <div class="tiles" v-if="result">
                <!-- Green-score : droit d'accès, puis valeurs, puis attente du premier calcul -->
                <div class="tile" :class="{'tile--locked': !canSeeLetters}">
                    <div v-if="!canSeeLetters"><div class="tl">Green-score</div><div class="tu">Réservé aux <a :href="urlLinkAboPro" target="_blank">{{ textLinkAboPro }}</a></div></div>
                    <template v-else-if="isDef(dg(result, 'singlescore.ecoscore_lettre'))">
                        <img :src="gsPicto(dg(result, 'singlescore.ecoscore_lettre'))" :alt="'Green-score ' + dg(result, 'singlescore.ecoscore_lettre').toUpperCase()">
                        <div><div class="tl">Green-score</div><div class="tv tnum">{{ fmt(dg(result, 'singlescore.ecoscore')) }}</div><div class="tu">sur 100</div></div>
                    </template>
                    <div v-else><div class="tl">Green-score</div><div class="tv">—</div><div class="tu">après le premier calcul</div></div>
                </div>
                <!-- Coût environnemental -->
                <div class="tile tile--ce" :class="{'tile--locked': !canSeeLetters}">
                    <div v-if="!canSeeLetters"><div class="tl">Coût environnemental</div><div class="tu">Réservé aux <a :href="urlLinkAboPro" target="_blank">{{ textLinkAboPro }}</a></div></div>
                    <template v-else-if="isDef(dg(result, 'singlescore.cout_environnemental'))">
                        <div class="ce-nb"><div class="tv tnum">{{ fmt(dg(result, 'singlescore.cout_environnemental')) }}</div><div class="tu">points</div></div>
                        <div><div class="tl">Coût environnemental</div><div class="tu tnum">{{ fmt(dg(result, 'singlescore.cout_environnemental100')) }} / 100 g</div><div class="hint"><span class="arrow" aria-hidden="true">▼</span>plus c'est bas, mieux c'est</div></div>
                    </template>
                    <div v-else><div class="tl">Coût environnemental</div><div class="tv">—</div><div class="tu">après le premier calcul</div></div>
                </div>
                <!-- Nutri-score -->
                <div class="tile" :class="{'tile--locked': !canSeeLetters}">
                    <div v-if="!canSeeLetters"><div class="tl">Nutri-score</div><div class="tu">Réservé aux <a :href="urlLinkAboPro" target="_blank">{{ textLinkAboPro }}</a></div></div>
                    <template v-else-if="isDef(dg(result, 'calcul.pointbfinalLETTRE'))">
                        <span class="score-letter" :class="'ns-' + dg(result, 'calcul.pointbfinalLETTRE', 'e')" role="img" :aria-label="'Nutri-score ' + dg(result, 'calcul.pointbfinalLETTRE').toUpperCase()">{{ String(dg(result, 'calcul.pointbfinalLETTRE', 'e')).toUpperCase() }}</span>
                        <div><div class="tl">Nutri-score</div><div class="tv tnum">{{ fmt(dg(result, 'calcul.nutriscore')) }}</div><div class="tu">points</div></div>
                    </template>
                    <div v-else><div class="tl">Nutri-score</div><div class="tv">—</div><div class="tu">après le premier calcul</div></div>
                </div>
                <!-- Carbone : règle de verrouillage propre (visiteur ou user sur un domaine) -->
                <div class="tile" :class="{'tile--locked': carbonLocked}">
                    <div v-if="carbonLocked"><div class="tl">Carbone</div><div class="tu">Réservé aux <a :href="urlLinkAboPro" target="_blank">{{ textLinkAboPro }}</a></div></div>
                    <template v-else-if="isDef(dg(result, 'gCO2e'))">
                        <span class="score-letter" :class="'l-' + dg(result, 'imgGCO2', 'e')" aria-hidden="true">{{ String(dg(result, 'imgGCO2', 'e')).toUpperCase() }}</span>
                        <div><div class="tl">Carbone</div><div class="tv tnum">{{ fmt(dg(result, 'gCO2e')) }}</div><div class="tu">g CO2e / couvert · {{ fmt(dg(result, 'gCO2e100')) }} / 100 g</div></div>
                    </template>
                    <div v-else><div class="tl">Carbone</div><div class="tv">—</div><div class="tu">après le premier calcul</div></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import format from './format';

export default {
    name: 'ScoreBar',
    mixins: [format],
    props: {
        name: {type: String, default: ''},
        saveState: {type: String, default: null},
        saveStateText: {type: String, default: ''},
        result: {type: Object, default: null},
        userRole: {type: String, default: ''},
        isDomain: {type: [String, Boolean], default: false},
        textLinkAboPro: {type: String, default: 'abonnements pro'},
        urlLinkAboPro: {type: String, default: ''},
        canSeeLetters: {type: Boolean, default: false},
    },
    computed: {
        // Règle existante (Calculateur.vue l. 819) : visiteur ou rôle user sur un domaine → « Réservé aux … »
        carbonLocked() {
            return (!this.userRole || this.userRole === 'user') && !!this.isDomain;
        },
    },
};
</script>
